"use client";

import { useActionState, useEffect, Suspense, useState } from "react";
import { useRouter, useSearchParams } from "next/navigation";
import Link from "next/link";
import Image from "next/image";
import { loginAction } from "@/app/actions/auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { AlertCircle, CheckCircle2, Loader2, Mail, Lock, User, Eye, EyeOff, Square, CheckSquare } from "lucide-react";

const initialState = { success: false, error: undefined };

function LoginForm() {
  const router = useRouter();
  const searchParams = useSearchParams();

  const [state, formAction, isPending] = useActionState(
    loginAction,
    initialState
  );

  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);

  useEffect(() => {
    if (state.success) {
      const redirectPath = state.redirectTo || searchParams.get("redirect") || "/dashboard";
      router.push(redirectPath);
    }
  }, [state.success, state.redirectTo, searchParams, router]);

  return (
    <div className="flex min-h-screen items-center justify-center bg-slate-100 p-4 lg:p-6">
      {/* Main Card */}
      <div className="flex w-full max-w-[900px] flex-col overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-xl shadow-slate-200/60 lg:flex-row">
        
        {/* Left Panel - Form */}
        <div className="flex w-full flex-col p-6 sm:p-8 lg:w-[45%] lg:p-10">
          {/* Logo */}
          <div className="flex justify-center mb-8">
            <div className="relative w-[200px] h-[70px]">
              <Image
                src="https://atm233178-s3user.vcos.cloudstorage.com.vn/portalupload/logo/LOGO%20NGANG%20SNS%20(2).pdf%20(1).png"
                alt="Company Logo"
                fill
                className="object-contain"
                priority
                unoptimized
              />
            </div>
          </div>

          <div className="flex-1">
            <div className="mb-6">
              <h1 className="text-2xl font-bold text-slate-800 mb-1">Sign in to the partner portal</h1>
              <p className="text-slate-500 text-sm">Manage orders, payments, invoices, and Dropbox teams.</p>
            </div>

            <form action={formAction} className="space-y-4">
              {state.error && (
                <div
                  className="flex items-start gap-3 p-3 bg-red-50 border border-red-100 rounded-lg text-red-600 text-sm"
                  role="alert"
                  aria-live="polite"
                >
                  <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
                  <span>{state.error}</span>
                </div>
              )}
              {searchParams.get("verified") && (
                <div
                  className="flex items-center gap-2 p-3 bg-green-50 border border-green-100 rounded-lg text-green-600 text-sm"
                  role="status"
                >
                  <CheckCircle2 className="w-4 h-4 shrink-0" />
                  Email verified successfully. You can now sign in.
                </div>
              )}

              <div className="space-y-1.5">
                <Label htmlFor="email" className="text-sm font-medium text-slate-700">
                  Email
                </Label>
                <div className="relative">
                  <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    placeholder="Email"
                    required
                    autoComplete="email"
                    className="h-11 pl-10 rounded-lg border-slate-200 text-slate-800 placeholder:text-slate-400 focus-visible:ring-2 focus-visible:ring-blue-500 focus:border-blue-500 transition-colors duration-200"
                  />
                </div>
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="password" className="text-sm font-medium text-slate-700">
                  Password
                </Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input
                    id="password"
                    name="password"
                    type={showPassword ? "text" : "password"}
                    placeholder="Password"
                    required
                    autoComplete="current-password"
                    className="h-11 pl-10 pr-12 rounded-lg border-slate-200 text-slate-800 placeholder:text-slate-400 focus-visible:ring-2 focus-visible:ring-blue-500 focus:border-blue-500 transition-colors duration-200"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-2 top-1/2 flex h-9 w-9 -translate-y-1/2 items-center justify-center rounded-md text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-600 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500"
                    aria-label={showPassword ? "Hide password" : "Show password"}
                  >
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>

              <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                <label className="flex min-h-11 cursor-pointer items-center gap-2">
                  <button
                    type="button"
                    onClick={() => setRememberMe(!rememberMe)}
                    className="flex h-9 w-9 items-center justify-center rounded-md text-slate-600 transition-colors hover:bg-slate-100 hover:text-blue-600 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500"
                    aria-pressed={rememberMe}
                  >
                    {rememberMe ? (
                      <CheckSquare className="w-4 h-4 text-blue-600" />
                    ) : (
                      <Square className="w-4 h-4" />
                    )}
                  </button>
                  <span className="text-sm text-slate-600">Remember Me</span>
                </label>
                <Link
                  href="/auth/forgot-password"
                  className="inline-flex min-h-11 items-center text-sm text-blue-600 transition-colors hover:text-blue-700"
                >
                  Forgot password?
                </Link>
              </div>

              <Button
                type="submit"
                size="lg"
                className="h-11 w-full rounded-lg bg-blue-600 text-sm font-bold uppercase tracking-wide text-white transition-colors duration-200 hover:bg-blue-700"
                disabled={isPending}
              >
                {isPending ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Signing in…
                  </>
                ) : (
                  "Sign in"
                )}
              </Button>
            </form>

            <div className="mt-4">
              <Button
                asChild
                variant="outline"
                size="lg"
                className="h-11 w-full rounded-lg border-blue-500 text-sm font-medium text-blue-600 hover:bg-blue-50"
              >
                <Link href="/auth/register">
                  Reseller Registration
                </Link>
              </Button>
            </div>
          </div>

          <p className="mt-6 text-xs text-center text-slate-400">
            © 2025 Dropbox Partner Program. All rights reserved.
          </p>
        </div>

        {/* Right Panel - Blue with Dropbox Logo */}
        <div className="relative flex w-full items-center justify-center overflow-hidden bg-blue-700 p-8 lg:w-[55%] lg:p-10">
          {/* Dropbox Logo - Centered */}
          <div className="relative w-32 h-32 lg:w-48 lg:h-48">
            <Image
              src="https://atm233178-s3user.vcos.cloudstorage.com.vn/portalupload/logo/DBX_Tab_128.png"
              alt="Dropbox Logo"
              fill
              className="object-contain"
              unoptimized
            />
          </div>

          {/* Decorative circles */}
          <div className="absolute inset-x-8 bottom-8 h-px bg-white/20" />
        </div>
      </div>
    </div>
  );
}

export default function LoginPage() {
  return (
    <Suspense>
      <LoginForm />
    </Suspense>
  );
}
